import {
  useDownloadReport,
  useJobCardDetail,
  useJobCardMaterialReport,
  useJobCardReadingsReport,
  useJobCardSignatureReport,
  useJobCardTaskReport,
} from "@features/wx/dispatcher/hooks";
import { wxUtils } from "@features/wx/utils";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import { Tabs } from "@shared/ui/tabs";
import {
  ArrowLeft,
  Building,
  Camera,
  CheckSquare,
  DollarSign,
  ExternalLink,
  FileText,
  HelpCircle,
  Loader2,
  Mail,
  Package,
  PenTool,
  Phone,
  User,
  Wrench,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HistoryServiceOrderKPI from "./dashboard/dashboard";

interface Document {
  id: number;
  type: number;
  typeDisplay: string;
  sop: number;
  remoteUtilityId: number;
  file: string | null;
  link: string | null;
  description: string;
  isActive: boolean;
}

// ====================== SEMANTIC COLOR CONFIGURATION ======================
const SEMANTIC_COLORS = {
  // 1. Status-Based Colors
  statusBased: {
    green: {
      text: "text-green-600",
      bg: "bg-green-600",
      bgHover: "hover:bg-green-700",
      border: "border-l-green-500",
      bgLight: "bg-green-100",
      textDark: "text-green-800",
      borderLight: "border-green-200",
    },
    red: {
      text: "text-red-600",
      textDark: "text-red-800",
      bg: "bg-red-600",
      bgHover: "hover:bg-red-700",
      border: "border-l-red-500",
      bgLight: "bg-red-100",
      borderLight: "border-red-200",
    },
    blue: {
      text: "text-blue-600",
      textDark: "text-blue-800",
      bg: "bg-blue-600",
      bgHover: "hover:bg-blue-700",
      border: "border-l-blue-500",
      bgLight: "bg-blue-100",
      borderLight: "border-blue-200",
    },
    orange: {
      text: "text-orange-600",
      textDark: "text-orange-800",
      bg: "bg-orange-600",
      bgHover: "hover:bg-orange-700",
      border: "border-l-orange-500",
      bgLight: "bg-orange-100",
      borderLight: "border-orange-200",
    },
  },

  // 2. Badge Color Mapping
  resourceTypes: {
    Document: "bg-blue-100 text-blue-800",
    instruction: "bg-blue-100 text-blue-800",
    "Video Link": "bg-red-100 text-red-800",
    Checklist: "bg-green-100 text-green-800",
    default: "bg-gray-100 text-gray-800",
  },

  // 3. Contextual Status Colors
  contextualStatus: {
    positive: "text-green-600", // Success states
    warning: "text-orange-600", // Warning states
    neutral: "text-gray-700", // Default states
  },

  // 4. Gray Scale Hierarchy
  grayScale: {
    primary: "text-gray-900", // Primary text, headings
    secondary: "text-gray-800", // Secondary headings
    body: "text-gray-700", // Body text
    supporting: "text-gray-600", // Supporting text
    labels: "text-gray-500", // Labels, metadata
    disabled: "text-gray-400", // Placeholders, disabled states
  },

  // 5. Background & Layout Colors
  backgrounds: {
    page: "bg-gray-50",
    card: "bg-white",
    headerGradient: "bg-gradient-to-r from-slate-50 to-gray-50",
  },

  // 6. Status Colors for Service Orders
  serviceOrderStatus: {
    Completed: "bg-green-100 text-green-800 border-green-200",
    Approved: "bg-green-100 text-green-800 border-green-200",
    Rejected: "bg-red-100 text-red-800 border-red-200",
    Cancelled: "bg-gray-100 text-gray-800 border-gray-200",
    "In Progress": "bg-blue-100 text-blue-800 border-blue-200",
    Pending: "bg-orange-100 text-orange-800 border-orange-200",
    default: "bg-blue-100 text-blue-800 border-blue-200",
  },
};

const CONFIG = {
  pagination: {
    itemsPerPage: 5,
    defaultPage: 1,
  },
  routes: {
    home: "/",
    serviceManagement: "/service-management",
    completedOrders: "/completed-orders",
    dispatcher: "/service-orders/dispatcher",
  },
  defaults: {
    currency: "$",
    fallbackText: "N/A",
    fallbackNumber: 0,
    fallbackCost: "0",
    status: "Completed",
    pageTitle: "Service Order",
  },
  tabs: {
    main: {
      details: "details",
      reports: "reports",
    },
    consumer: {
      consumerMeter: "consumer-meter",
      technician: "technician",
    },
    details: {
      overview: "overview",
      tasks: "tasks",
      materials: "materials",
      readings: "readings",
      help: "help",
    },
    reports: {
      overview: "overview",
      tasks: "tasks",
      materials: "materials",
      readings: "readings",
      signature: "signature",
      technicians: "technicians",
    },
  },

  // KPI Cards using semantic colors

  serviceOrderFields: [
    { key: "soName", label: "SO Name" },
    { key: "jobCardNo", label: "Short Name" },
    { key: "createdOn", label: "Created On" },
    { key: "assignedOn", label: "Assigned On" },
    { key: "expectedCompletion", label: "Expected Completion" },
    { key: "utilityServices", label: "Utility Services" },
    { key: "area", label: "Area" },
    { key: "subarea", label: "Subarea" },
    { key: "premise", label: "Premise" },
    { key: "taxRate", label: "Tax Rate" },
    { key: "description", label: "Description" },
    { key: "remark", label: "Remark" },
  ],

  consumerFields: [
    { key: "consumerName", label: "Consumer Name" },
    { key: "accountNo", label: "Account No" },
    { key: "phone", label: "Phone", icon: Phone },
  ],

  meterFields: [
    { key: "meterNo", label: "Meter No" },
    { key: "deviceNo", label: "Device No" },
    { key: "premise", label: "Premise", source: "serviceOrderData" },
    { key: "physicalStatusDisplay", label: "Physical Status" },
    { key: "statusDisplay", label: "Assignment Status" },
    { key: "readingStatus", label: "Reading Status" },
  ],

  technicianFields: [
    { key: "technicianName", label: "Technician Name" },
    { key: "fid", label: "FID" },
    { key: "phone", label: "Phone", icon: Phone },
    { key: "role", label: "Role" },
    { key: "email", label: "Email", icon: Mail },
  ],

  costOverviewItems: [
    { key: "labourCost", label: "Labour Cost" },
    { key: "materialCost", label: "Material Cost" },
    { key: "otherCost", label: "Other Cost" },
  ],

  effortItems: [
    { key: "efforts", label: "Efforts", unit: "h" },
    { key: "downtime", label: "Downtime", unit: " mins" },
    { key: "requiredFieldforce", label: "Required Fieldforce" },
  ],

  signatureTypes: [
    { key: "tpi", title: "TPI Signature" },
    { key: "consumer", title: "Consumer Signature" },
  ],

  messages: {
    noData: "No data available",
    noTechnician: "No technician assigned",
    signatureCaptured: "Signature captured",
    verified: "Verified",
  },
};

// ====================== UTILITY FUNCTIONS ======================
const formatValue = (
  value,
  formatter = "text",
  currency = CONFIG.defaults.currency
) => {
  switch (formatter) {
    case "currency":
      return `${currency} ${value || CONFIG.defaults.fallbackCost}`;
    case "text":
      return value || CONFIG.defaults.fallbackText;
    case "number":
      return value || CONFIG.defaults.fallbackNumber;
    default:
      return value || CONFIG.defaults.fallbackText;
  }
};

const getValueFromPath = (obj, path) => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

const getStatusColorClass = (status) => {
  return (
    SEMANTIC_COLORS.serviceOrderStatus[status] ||
    SEMANTIC_COLORS.serviceOrderStatus.default
  );
};

const getResourceTypeColor = (resourceType) => {
  return (
    SEMANTIC_COLORS.resourceTypes[resourceType] ||
    SEMANTIC_COLORS.resourceTypes.default
  );
};

const getDowntimeColorClass = (downtimeMins) => {
  if (downtimeMins > 0) {
    return SEMANTIC_COLORS.contextualStatus.warning;
  }
  return SEMANTIC_COLORS.contextualStatus.neutral;
};

// ====================== BASE COLUMN CONFIGURATIONS ======================
const createBaseColumns = () => ({
  taskName: {
    key: "taskName",
    header: "Task Name",
    render: (value) => (
      <span className={`font-medium ${SEMANTIC_COLORS.grayScale.primary}`}>
        {formatValue(value)}
      </span>
    ),
  },
  materialName: {
    key: "name",
    header: "Name",
    render: (value) => (
      <span className={`font-medium ${SEMANTIC_COLORS.grayScale.primary}`}>
        {formatValue(value)}
      </span>
    ),
  },
  size: {
    key: "size",
    header: "Size",
    render: (value) => (
      <span className={SEMANTIC_COLORS.grayScale.body}>
        {formatValue(value)}
      </span>
    ),
  },
  quantity: {
    key: "quantity",
    header: "Quantity",
    render: (value) => (
      <span className={SEMANTIC_COLORS.grayScale.body}>
        {formatValue(value, "number")}
      </span>
    ),
  },
  cost: {
    key: "cost",
    header: "Cost",
    render: (value) => (
      <span
        className={`font-semibold ${SEMANTIC_COLORS.statusBased.green.text}`}
      >
        {formatValue(value, "currency")}
      </span>
    ),
  },
  unit: {
    key: "unit",
    header: "Unit",
    render: (value) => (
      <span className={SEMANTIC_COLORS.grayScale.body}>
        {formatValue(value)}
      </span>
    ),
  },
  photoRequired: {
    key: "photoRequired",
    header: "Photo Required",
    render: (value) => (
      <div className="text-center">
        {value ? (
          <Camera
            className={`h-4 w-4 ${SEMANTIC_COLORS.statusBased.green.text} mx-auto`}
          />
        ) : (
          <span className={SEMANTIC_COLORS.grayScale.disabled}>-</span>
        )}
      </div>
    ),
  },
  checkbox: {
    key: "action",
    header: "Action",
    render: () => (
      <div className="text-center">
        <Checkbox defaultChecked />
      </div>
    ),
  },
  photoPlaceholder: {
    key: "photo",
    header: "Photo",
    render: (value, row) => (
      <div className="text-center">
        {row.photoRequired && (
          <div
            className={`w-8 h-8 ${SEMANTIC_COLORS.backgrounds.card} rounded border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto`}
          >
            <Camera
              className={`h-3 w-3 ${SEMANTIC_COLORS.grayScale.disabled}`}
            />
          </div>
        )}
      </div>
    ),
  },
});

// ====================== REUSABLE COMPONENTS ======================
const CostOverview = ({ overview, showComparison = false, currency }) => {
  if (!overview) return <div>{CONFIG.messages.noData}</div>;

  const renderCost = (item) => (
    <div key={item.key} className="flex justify-between">
      <span className={SEMANTIC_COLORS.grayScale.supporting}>
        {item.label}:
      </span>
      {showComparison ? (
        <div className="flex gap-4">
          <span className={SEMANTIC_COLORS.statusBased.blue.text}>
            {formatValue(overview[item.key], "currency", currency)} (Est)
          </span>
          <span className={SEMANTIC_COLORS.statusBased.green.text}>
            {formatValue(overview[item.key], "currency", currency)} (Actual)
          </span>
        </div>
      ) : (
        <span className="font-medium">
          {formatValue(overview[item.key], "currency", currency)}
        </span>
      )}
    </div>
  );

  const renderEffort = (item) => (
    <div key={item.key} className="flex justify-between">
      <span className={SEMANTIC_COLORS.grayScale.supporting}>
        {item.label}:
      </span>
      {showComparison && (item.key === "efforts" || item.key === "downtime") ? (
        <div className="flex gap-4">
          <span className={SEMANTIC_COLORS.statusBased.blue.text}>
            {formatValue(overview[item.key])}
            {item.unit} (Est)
          </span>
          <span className={SEMANTIC_COLORS.statusBased.green.text}>
            {formatValue(overview[item.key])}
            {item.unit} (Actual)
          </span>
        </div>
      ) : (
        <span className="font-medium">
          {formatValue(overview[item.key])}
          {item.unit || ""}
        </span>
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-3">
        {CONFIG.costOverviewItems.map(renderCost)}
        <div className="flex justify-between border-t pt-2">
          <span
            className={`${SEMANTIC_COLORS.grayScale.primary} font-semibold`}
          >
            Total Cost:
          </span>
          {showComparison ? (
            <div className="flex gap-4">
              <span
                className={`${SEMANTIC_COLORS.statusBased.blue.text} font-bold`}
              >
                {formatValue(overview.totalCost, "currency")} (Est)
              </span>
              <span
                className={`${SEMANTIC_COLORS.statusBased.green.text} font-bold`}
              >
                {formatValue(overview.totalCost, "currency", currency)} (Actual)
              </span>
            </div>
          ) : (
            <span className="font-bold">
              {formatValue(overview.totalCost, "currency", currency)}
            </span>
          )}
        </div>
      </div>
      <div className="space-y-3">{CONFIG.effortItems.map(renderEffort)}</div>
    </div>
  );
};

const DetailField = ({ label, value, icon }) => (
  <div>
    <label
      className={`text-sm font-medium ${SEMANTIC_COLORS.grayScale.labels} block mb-1`}
    >
      {label}
    </label>
    <div
      className={`${SEMANTIC_COLORS.grayScale.primary} flex items-center gap-2`}
    >
      {icon &&
        React.createElement(icon, {
          className: `h-4 w-4 ${SEMANTIC_COLORS.grayScale.labels}`,
        })}
      {formatValue(value)}
    </div>
  </div>
);

// Wrapper component for two-column layout
const DetailFieldsGrid = ({ children }) => (
  <div className="grid grid-cols-2 gap-6">{children}</div>
);

const SignatureSection = ({ signatureData, signatureLoading }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          {
            title: "TPI Signature",
            type: "tpi",
            imageUrl: signatureData?.result?.fieldForceSignature,
            isApproved: signatureData?.result?.isFieldForceSignApproved,
          },
          {
            title: "Consumer Signature",
            type: "consumer",
            imageUrl: signatureData?.result?.requestSignature,
            isApproved: signatureData?.result?.isRequestSignApproved,
          },
        ].map(({ title, type, imageUrl, isApproved }) => (
          <div key={type}>
            <h4
              className={`text-lg font-semibold ${SEMANTIC_COLORS.grayScale.primary} mb-3 flex items-center gap-2`}
            >
              <PenTool
                className={`h-4 w-4 ${SEMANTIC_COLORS.statusBased.orange.text}`}
              />
              {title}
            </h4>

            {signatureLoading ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <div className="flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400 mr-2" />
                  <span className="text-gray-500">Loading signature...</span>
                </div>
              </div>
            ) : imageUrl ? (
              <div className="border-2 border-solid border-gray-300 rounded-lg p-4 text-center bg-white">
                <img
                  src={imageUrl}
                  alt={`${title}`}
                  className="max-w-full h-32 object-contain mx-auto cursor-pointer border rounded"
                  onClick={() => window.open(imageUrl, "_blank")}
                  onError={(e) => {}}
                />
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                <PenTool
                  className={`h-8 w-8 ${SEMANTIC_COLORS.grayScale.disabled} mx-auto mb-2`}
                />
                <p className={SEMANTIC_COLORS.grayScale.labels}>
                  No signature captured
                </p>
                <div className="mt-3">
                  <Checkbox checked={false} disabled />
                  <span
                    className={`ml-2 text-sm ${SEMANTIC_COLORS.grayScale.supporting}`}
                  >
                    Not signed
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {signatureData?.result?.completedDate && (
        <div className="text-center text-sm text-gray-500 mt-4">
          Completed on:{" "}
          {new Date(signatureData.result.completedDate).toLocaleString()}
        </div>
      )}
    </div>
  );
};

// ====================== MAIN COMPONENT ======================
const AssetHistoryServiceOrderViewPage = () => {
  const { id, serviceType } = useParams();

  const navigate = useNavigate();
  const currency = useCurrency();
  const [activeMainTab, setActiveMainTab] = useState(CONFIG.tabs.main.details);
  const [currentPage, setCurrentPage] = useState(CONFIG.pagination.defaultPage);
  const downloadCSV = useDownloadReport();

  // ✅ KEEP: Existing job card detail data (for Details tab)
  const {
    data: jobCardResponse,
    isLoading: loading,
    error,
    refetch,
  } = useJobCardDetail({ id });

  // ✅ NEW: Report data hooks (only for Reports tab)
  const {
    data: taskReportData,
    isLoading: taskLoading,
    error: taskError,
  } = useJobCardTaskReport(id, { enabled: !!id });

  const {
    data: materialReportData,
    isLoading: materialLoading,
    error: materialError,
  } = useJobCardMaterialReport(id, { enabled: !!id });

  const {
    data: readingsReportData,
    isLoading: readingsLoading,
    error: readingsError,
  } = useJobCardReadingsReport(id, { enabled: !!id });

  const {
    data: signatureReportData,
    isLoading: signatureLoading,
    error: signatureError,
  } = useJobCardSignatureReport(id, { enabled: !!id });

  const handleDownload = () => {
    if (!id) return;
    downloadCSV.mutateAsync({
      reportId: Number(id),
      tabName: "completed",
    });
  };

  // ✅ KEEP: Existing data transformation (for Details tab)
  const transformedData = useMemo(() => {
    if (!jobCardResponse) return null;
    return wxUtils.transformJobCardDetail(jobCardResponse);
  }, [jobCardResponse]);

  // ✅ NEW: Transform report data (only for Reports tab)
  const transformedReportData = useMemo(() => {
    const tasks =
      taskReportData?.result?.task?.map((task, index) => {
        return {
          taskId: index,
          taskName: task.name,
          tatMins: task.time,
          downtimeMins: parseInt(task.downtime) || 0,
          photoRequired: task.isFile,
          isComplete: task.isComplete,
          status: task.status,
          taskFile: task.taskFile,
          photo: task.taskFile,
          executed: task.isComplete,
        };
      }) || [];

    const materials =
      materialReportData?.result?.material?.map((material, index) => {
        return {
          materialId: index,
          name: material.name || "N/A",
          size: material.size || "N/A",
          quantity: material.quantity || "0",
          actualQuantity: material.actualQuantity || material.quantity || "0",
          cost: material.cost || "0",
          unit: material.unit || "pcs",
        };
      }) || [];

    const readings =
      readingsReportData?.result?.reading?.map((reading, index) => {
        return {
          readingId: index,
          key: reading.key,
          name: reading.name,
          unit: reading.unit,
          minimum: reading.minimum,
          maximum: reading.maximum,
          range: `${reading.minimum}-${reading.maximum}`,
          reading: reading.readings,
          readingFile: reading.readingFile,
          photoRequired: !!reading.readingFile,
        };
      }) || [];

    return { tasks, materials, readings };
  }, [taskReportData, materialReportData, readingsReportData]);

  // ✅ KEEP: Extract existing data for Details tab
  const {
    serviceOrderData,
    consumerDetails,
    meterDetails,
    assetDetails,
    technicianDetails,
    jobOverview,
    tasksData, // ← Keep for Details tab
    materialsData, // ← Keep for Details tab
    readingsData, // ← Keep for Details tab
    helpResourcesData,
  } = transformedData || {};

  // Service type configuration - ADD THIS ENTIRE BLOCK
  const serviceTypeConfig = {
    Asset: {
      title: "Asset Details",
      icon: "🏗️",
      fields: {
        id: "Asset ID",
        name: "Asset Name",
        location: "Location",
        condition: "Condition",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
    Facility: {
      title: "Facility Details",
      icon: "🏢",
      fields: {
        id: "Facility ID",
        name: "Facility Name",
        location: "Location",
        condition: "Condition",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
    System: {
      title: "System Details",
      icon: "⚙️",
      fields: {
        id: "System ID",
        name: "System Name",
        location: "Location",
        condition: "Condition Score",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
    Network: {
      title: "Network Details",
      icon: "🌐",
      fields: {
        id: "Network ID",
        name: "Network Name",
        location: "Location",
        condition: "Condition",
        riskScore: "Risk Score",
        status: "Status",
      },
    },
  };

  // DetailRow component - ADD THIS
  const DetailRow = ({ label, value }) => (
    <div>
      <label className="text-sm font-medium text-gray-500">{label}</label>
      <p className="text-base text-gray-900">{value || "N/A"}</p>
    </div>
  );

  // Add after useParams destructuring
  const validServiceType =
    serviceType && serviceType in serviceTypeConfig ? serviceType : "Asset";
  const currentServiceConfig = serviceTypeConfig[validServiceType];

  const {
    tasks: reportTasks,
    materials: reportMaterials,
    readings: reportReadings,
  } = transformedReportData;

  // Dynamic Service & Technician tab components
  const serviceTechnicianTabComponents = useMemo(
    () => ({
      "service-details": {
        label: currentServiceConfig.title,
        icon: <Building className="h-4 w-4" />,
        component: (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <DetailRow
                label={currentServiceConfig.fields.id}
                value={assetDetails?.assetId}
              />
              <DetailRow
                label={currentServiceConfig.fields.name}
                value={assetDetails?.assetName}
              />
            </div>
            <DetailRow
              label={currentServiceConfig.fields.location}
              value={assetDetails?.location}
            />
            <div className="grid grid-cols-2 gap-4">
              <DetailRow
                label={currentServiceConfig.fields.condition}
                value={assetDetails?.condition}
              />
              <DetailRow
                label={currentServiceConfig.fields.riskScore}
                value={assetDetails?.rpn}
              />
            </div>
            <DetailRow
              label={currentServiceConfig.fields.status}
              value={assetDetails?.status}
            />
          </div>
        ),
      },
      technician: {
        label: "Assigned Technician",
        icon: <User className="h-4 w-4" />,
        component: (
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="h-4 w-4 text-green-600" />
              Assignee Details
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Technician Name
                </label>
                <div className="text-gray-900">
                  {technicianDetails?.technicianName || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  FID
                </label>
                <div className="text-gray-900 font-mono">
                  {technicianDetails?.fid || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Phone
                </label>
                <div className="flex items-center gap-2 text-gray-900">
                  <Phone className="h-4 w-4 text-gray-500" />
                  {technicianDetails?.phone || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Role
                </label>
                <div className="text-gray-900">
                  {technicianDetails?.role || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Email
                </label>
                <div className="text-gray-900">
                  {technicianDetails?.email || "N/A"}
                </div>
              </div>
            </div>
          </div>
        ),
      },
    }),
    [currentServiceConfig, assetDetails, technicianDetails]
  );

  // Pagination helpers
  const createPaginationConfig = (data = []) => {
    const totalItems = data.length;
    const totalPages = Math.max(
      1,
      Math.ceil(totalItems / CONFIG.pagination.itemsPerPage)
    );
    const safePage = Math.max(1, Math.min(currentPage, totalPages));

    return {
      currentPage: safePage,
      totalPages,
      totalItems,
      itemsPerPage: CONFIG.pagination.itemsPerPage,
      startItem:
        totalItems === 0
          ? 0
          : (safePage - 1) * CONFIG.pagination.itemsPerPage + 1,
      endItem:
        totalItems === 0
          ? 0
          : Math.min(safePage * CONFIG.pagination.itemsPerPage, totalItems),
    };
  };

  const getPaginatedData = (data = []) => {
    if (!Array.isArray(data)) return [];
    const startIndex = (currentPage - 1) * CONFIG.pagination.itemsPerPage;
    return data.slice(startIndex, startIndex + CONFIG.pagination.itemsPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderDocumentLink = (document: Document) => {
    console.log("debug document", document);
    if (document.file) {
      return (
        <a
          href={document.file}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <FileText className="h-4 w-4 mr-1" />
          View File
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    if (document.link) {
      return (
        <a
          href={document.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          Open Link
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    return <span className="text-muted-foreground">No link/file</span>;
  };

  const handleBack = () => navigate(CONFIG.routes.dispatcher);

  // Column configurations
  const baseColumns = useMemo(() => createBaseColumns(), []);

  const columnConfigs = useMemo(
    () => ({
      tasks: [
        baseColumns.taskName,
        {
          key: "tatMins",
          header: "TAT (Mins)",
          render: (value) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value, "number")}
            </span>
          ),
        },
        {
          key: "downtimeMins",
          header: "Downtime (Mins)",
          render: (value) => (
            <span className={getDowntimeColorClass(value)}>
              {formatValue(value, "number")}
            </span>
          ),
        },
        baseColumns.photoRequired,
      ],
      materials: [
        baseColumns.materialName,
        baseColumns.size,
        baseColumns.quantity,
        baseColumns.cost,
      ],
      readings: [
        baseColumns.materialName,
        baseColumns.unit,
        {
          key: "range",
          header: "Range",
          render: (value) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value)}
            </span>
          ),
        },
        baseColumns.photoRequired,
      ],
      helpResources: [
        {
          key: "resourceName",
          header: "Resource Name",
          render: (value) => (
            <span
              className={`font-medium ${SEMANTIC_COLORS.statusBased.blue.text} hover:${SEMANTIC_COLORS.statusBased.blue.textDark} cursor-pointer`}
            >
              {formatValue(value)}
            </span>
          ),
        },
        {
          key: "resourceType",
          header: "Resource Type",
          type: "badge",
          badgeConfig: {
            getClassName: getResourceTypeColor,
          },
        },
        {
          key: "details",
          header: "Details",
          render: (value: string, row: Document) => renderDocumentLink(row),
        },
      ],
    }),
    [baseColumns]
  );

  // Create table tab helper
  const createTableTab = (label, icon, data, columns, rowKey) => ({
    label,
    icon,
    count: data?.length || 0,
    component: (
      <GenericTable
        data={getPaginatedData(data)}
        columns={columns}
        rowKey={rowKey}
        pagination={createPaginationConfig(data)}
        onPageChange={handlePageChange}
        emptyMessage={`No ${label.toLowerCase()} found`}
      />
    ),
  });

  // Details tab components
  const detailsTabComponents = useMemo(
    () => ({
      [CONFIG.tabs.details.overview]: {
        label: "Overview",
        icon: <DollarSign className="h-4 w-4" />,
        component: <CostOverview overview={jobOverview} currency={currency} />,
      },
      [CONFIG.tabs.details.tasks]: createTableTab(
        "Tasks",
        <CheckSquare className="h-4 w-4" />,
        tasksData,
        columnConfigs.tasks,
        "taskId"
      ),
      [CONFIG.tabs.details.materials]: createTableTab(
        "Materials",
        <Package className="h-4 w-4" />,
        materialsData,
        columnConfigs.materials,
        "materialId"
      ),
      [CONFIG.tabs.details.readings]: createTableTab(
        "Readings",
        <Wrench className="h-4 w-4" />,
        readingsData,
        columnConfigs.readings,
        "readingId"
      ),
      [CONFIG.tabs.details.help]: createTableTab(
        "Help",
        <HelpCircle className="h-4 w-4" />,
        helpResourcesData,
        columnConfigs.helpResources,
        "resourceId"
      ),
    }),
    [
      columnConfigs,
      currentPage,
      tasksData,
      materialsData,
      readingsData,
      helpResourcesData,
      jobOverview,
    ]
  );

  // Reports columns with additional fields
  const reportsColumns = useMemo(
    () => ({
      tasks: [
        ...columnConfigs.tasks.slice(0, -1), // Remove photoRequired column
        {
          key: "photo",
          header: "Photo",
          render: (value, row) => (
            <div className="text-center">
              {row.taskFile ? (
                <img
                  src={row.taskFile}
                  alt="Task photo"
                  className="w-8 h-8 object-cover rounded border-2 border-gray-300 mx-auto cursor-pointer"
                  onClick={() => window.open(row.taskFile, "_blank")}
                />
              ) : (
                <div className="w-8 h-8 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
                  <Camera className="h-3 w-3 text-gray-400" />
                </div>
              )}
            </div>
          ),
        },
        {
          key: "executed",
          header: "Executed",
          render: (value, row) => (
            <div className="text-center">
              <Checkbox checked={row.isComplete} disabled />
            </div>
          ),
        },
        baseColumns.checkbox,
      ],
      materials: [
        baseColumns.materialName,
        baseColumns.size,
        {
          key: "quantity",
          header: "Est Quantity",
          render: (value) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value, "number")}
            </span>
          ),
        },
        {
          key: "actualQuantity",
          header: "Actual Used Quantity",
          render: (value) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value, "number")}
            </span>
          ),
        },
        {
          key: "cost",
          header: "Cost Per Unit",
          render: (value) => (
            <span
              className={`font-semibold ${SEMANTIC_COLORS.statusBased.green.text}`}
            >
              {formatValue(value, "currency")}
            </span>
          ),
        },
        baseColumns.checkbox,
      ],
      readings: [
        baseColumns.materialName,
        baseColumns.unit,
        {
          key: "range",
          header: "Range",
          render: (value) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value)}
            </span>
          ),
        },
        {
          key: "minimum",
          header: "Minimum",
          render: (value, row) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value)}
            </span>
          ),
        },
        {
          key: "maximum",
          header: "Maximum",
          render: (value, row) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value)}
            </span>
          ),
        },
        {
          key: "reading",
          header: "Reading",
          render: (value) => (
            <span className={SEMANTIC_COLORS.grayScale.body}>
              {formatValue(value, "number")}
            </span>
          ),
        },
        {
          key: "photo",
          header: "Photo",
          render: (value, row) => (
            <div className="text-center">
              {row.readingFile ? (
                <img
                  src={row.readingFile}
                  alt="Reading photo"
                  className="w-8 h-8 object-cover rounded border-2 border-gray-300 mx-auto cursor-pointer"
                  onClick={() => window.open(row.readingFile, "_blank")}
                />
              ) : (
                <div className="w-8 h-8 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
                  <Camera className="h-3 w-3 text-gray-400" />
                </div>
              )}
            </div>
          ),
        },
        baseColumns.checkbox,
      ],
    }),
    [baseColumns, columnConfigs]
  );

  // Reports tab components
  const reportsTabComponents = useMemo(
    () => ({
      [CONFIG.tabs.reports.overview]: {
        label: "Overview",
        icon: <DollarSign className="h-4 w-4" />,
        component: (
          <CostOverview
            overview={jobOverview}
            showComparison
            currency={currency}
          />
        ),
      },
      [CONFIG.tabs.reports.tasks]: createTableTab(
        "Tasks",
        <CheckSquare className="h-4 w-4" />,
        reportTasks, // ← Uses report data
        reportsColumns.tasks,
        "taskId"
      ),
      [CONFIG.tabs.reports.materials]: createTableTab(
        "Materials",
        <Package className="h-4 w-4" />,
        reportMaterials, // ← Uses report data
        reportsColumns.materials,
        "materialId"
      ),
      [CONFIG.tabs.reports.readings]: createTableTab(
        "Readings",
        <Wrench className="h-4 w-4" />,
        reportReadings, // ← Uses report data
        reportsColumns.readings,
        "readingId"
      ),
      [CONFIG.tabs.reports.signature]: {
        label: "Signature",
        icon: <PenTool className="h-4 w-4" />,
        component: (
          <SignatureSection
            signatureData={signatureReportData}
            signatureLoading={signatureLoading}
          />
        ),
      },
      [CONFIG.tabs.reports.technicians]: {
        label: "Technicians",
        icon: <User className="h-4 w-4" />,
        component: (
          <GenericTable
            data={technicianDetails ? [technicianDetails] : []}
            columns={[
              {
                key: "fid",
                header: "FID",
                render: (value) => (
                  <span
                    className={`font-medium ${SEMANTIC_COLORS.grayScale.primary}`}
                  >
                    {formatValue(value)}
                  </span>
                ),
              },
              {
                key: "technicianName",
                header: "Name",
                render: (value) => (
                  <span className={SEMANTIC_COLORS.grayScale.body}>
                    {formatValue(value)}
                  </span>
                ),
              },
              {
                key: "phone",
                header: "Phone",
                render: (value) => (
                  <span className={SEMANTIC_COLORS.grayScale.body}>
                    {formatValue(value)}
                  </span>
                ),
              },
              {
                key: "role",
                header: "Role",
                render: (value) => (
                  <span className={SEMANTIC_COLORS.grayScale.body}>
                    {formatValue(value)}
                  </span>
                ),
              },
              baseColumns.checkbox,
            ]}
            rowKey="fid"
            emptyMessage={CONFIG.messages.noTechnician}
            onPageChange={handlePageChange}
          />
        ),
      },
    }),
    [
      reportsColumns,
      currentPage,
      reportTasks, // ← Report data
      reportMaterials, // ← Report data
      reportReadings, // ← Report data
      technicianDetails,
      jobOverview,
      baseColumns,
      signatureReportData,
      signatureLoading,
    ]
  );

  // Consumer tab components

  return (
    <div className={`min-h-screen ${SEMANTIC_COLORS.backgrounds.page}`}>
      {/* Page Header */}
      <div className={`${SEMANTIC_COLORS.backgrounds.card} border-b`}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-start justify-between">
            <h1
              className={`text-3xl font-bold ${SEMANTIC_COLORS.grayScale.primary} mb-2`}
            >
              Completed Service Order -{" "}
              {serviceOrderData?.soName || CONFIG.defaults.pageTitle}
            </h1>

            <div className="flex items-center gap-3">
              <Badge
                className={`${getStatusColorClass(
                  serviceOrderData?.status || CONFIG.defaults.status
                )} px-3 py-1 text-sm font-medium border`}
              >
                {serviceOrderData?.status || CONFIG.defaults.status}
              </Badge>
              <Button
                onClick={handleDownload}
                className={`${SEMANTIC_COLORS.statusBased.blue.bg} ${SEMANTIC_COLORS.statusBased.blue.bgHover} text-white`}
              >
                <FileText className="h-4 w-4 mr-2" />
                View Report
              </Button>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <HistoryServiceOrderKPI
          jobOverview={jobOverview}
          serviceOrderData={serviceOrderData}
          className="mb-8"
        />

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Service Order Details Card */}
          <Card>
            <CardHeader
              className={`${SEMANTIC_COLORS.backgrounds.headerGradient} p-2`}
            >
              <CardTitle
                className={`text-xl font-semibold ${SEMANTIC_COLORS.grayScale.secondary}`}
              >
                Service Order Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-6">
                {CONFIG.serviceOrderFields.map((field) => (
                  <DetailField
                    key={field.key}
                    label={field.label}
                    value={serviceOrderData?.[field.key]}
                    icon={undefined}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4">
            <CardHeader
              className={`${SEMANTIC_COLORS.backgrounds.headerGradient} p-2`}
            >
              <CardTitle
                className={`text-xl font-semibold ${SEMANTIC_COLORS.grayScale.secondary} flex items-center gap-2`}
              >
                <span>{currentServiceConfig.icon}</span>
                {currentServiceConfig.title} & Assigned Technician
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Tabs
                defaultValue="service-details"
                idPrefix={`${validServiceType.toLowerCase()}-details`}
                tabComponents={serviceTechnicianTabComponents}
                tabsListClassName="grid w-full grid-cols-2"
                disableUrlSync={true}
              />
            </CardContent>
          </Card>
        </div>

        {/* Job Requirements & Reports Card */}
        <Card>
          <CardHeader
            className={`${SEMANTIC_COLORS.backgrounds.headerGradient} p-2`}
          >
            <CardTitle
              className={`text-xl font-semibold ${SEMANTIC_COLORS.grayScale.secondary}`}
            >
              Job Requirements & Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Main Tab Switcher */}
            <div className="mb-6">
              <div className="grid w-full grid-cols-2">
                <button
                  onClick={() => setActiveMainTab(CONFIG.tabs.main.details)}
                  className={`py-2 px-4 text-sm font-medium transition-colors ${
                    activeMainTab === CONFIG.tabs.main.details
                      ? "bg-gradient-to-r from-slate-50 to-gray-50 border-b-2 border-blue-500"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveMainTab(CONFIG.tabs.main.reports)}
                  className={`py-2 px-4 text-sm font-medium transition-colors ${
                    activeMainTab === CONFIG.tabs.main.reports
                      ? "bg-gradient-to-r from-slate-50 to-gray-50 border-b-2 border-blue-500"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Reports
                </button>
              </div>
            </div>

            {/* Content based on active main tab */}
            <div className="min-h-[400px]">
              {activeMainTab === CONFIG.tabs.main.details && (
                <Tabs
                  defaultValue={CONFIG.tabs.details.overview}
                  idPrefix="details"
                  tabComponents={detailsTabComponents}
                  tabsListClassName="grid w-full grid-cols-5"
                  disableUrlSync={true}
                />
              )}

              {activeMainTab === CONFIG.tabs.main.reports && (
                <>
                  {taskLoading || materialLoading || readingsLoading ? (
                    <div className="flex items-center justify-center h-64">
                      <Loader2 className="h-8 w-8 animate-spin text-gray-400 mr-2" />
                      <span className="text-gray-500">
                        Loading report data...
                      </span>
                    </div>
                  ) : (
                    <Tabs
                      defaultValue={CONFIG.tabs.reports.overview}
                      idPrefix="reports"
                      tabComponents={reportsTabComponents}
                      tabsListClassName="grid w-full grid-cols-6"
                      disableUrlSync={true}
                    />
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssetHistoryServiceOrderViewPage;
